import React, { useEffect, useState, useMemo } from 'react';
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Link as MuiLink,
  Skeleton,
  Toolbar,
  Typography,
  Alert,
} from '@mui/material';
import { Email, WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants';
import Logo from '@components/Logo';
import { apiService } from '@services/api';
import logger from '@utils/logger';

export type PublicSupportContact = {
  email: string;
  whatsapp: string;
  hours: string;
};

function digitsOnly(input: string): string {
  return input.replace(/\D/g, '');
}

/** URL wa.me só com dígitos (evita XSS em href). Opcional: prefixo 55 para números BR sem DDI. */
function buildWhatsAppHref(raw: string): string | null {
  let d = digitsOnly(raw);
  if ((d.length === 10 || d.length === 11) && !d.startsWith('55')) {
    d = `55${d}`;
  }
  if (d.length < 10 || d.length > 15) return null;
  return `https://wa.me/${d}`;
}

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contact, setContact] = useState<PublicSupportContact | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiService.getPublicSupportContact();
        if (cancelled) return;
        if (data?.success && data.data) {
          setContact({
            email: String(data.data.email ?? '').trim(),
            whatsapp: String(data.data.whatsapp ?? '').trim(),
            hours: String(data.data.hours ?? '').trim(),
          });
        } else {
          setError('Não foi possível carregar os dados de contato.');
        }
      } catch (e) {
        logger.warn('Contact page: falha ao buscar suporte público', e);
        if (!cancelled) setError('Não foi possível carregar os dados de contato. Tente novamente em instantes.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const waHref = useMemo(() => (contact?.whatsapp ? buildWhatsAppHref(contact.whatsapp) : null), [contact]);

  const hasAnyContact =
    !!contact && (contact.email.length > 0 || contact.whatsapp.length > 0);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Logo />
          </Box>
          <Button variant="outlined" onClick={() => navigate(ROUTES.LOGIN)}>
            Entrar
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          pt: { xs: 5, md: 7 },
          pb: { xs: 4, md: 6 },
          background: 'linear-gradient(135deg, #f7f8fc 0%, #e2e8f0 50%, #d1d8e4 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 800,
              textAlign: 'center',
              color: '#21316a',
              fontSize: { xs: '1.85rem', sm: '2.25rem', md: '2.75rem' },
              mb: 1,
            }}
          >
            Fale com a Vover
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              maxWidth: 640,
              mx: 'auto',
              mb: 4,
              lineHeight: 1.6,
            }}
          >
            Canais oficiais de atendimento. Os dados abaixo são mantidos pela nossa equipe e atualizados periodicamente.
          </Typography>

          {error && (
            <Alert severity="warning" sx={{ maxWidth: 720, mx: 'auto', mb: 3 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Email sx={{ color: 'primary.main', fontSize: 32 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      E-mail
                    </Typography>
                  </Box>
                  {loading ? (
                    <Skeleton variant="text" height={28} />
                  ) : contact?.email ? (
                    <MuiLink href={`mailto:${encodeURIComponent(contact.email)}`} underline="hover" sx={{ fontSize: '1.05rem', wordBreak: 'break-word' }}>
                      {contact.email}
                    </MuiLink>
                  ) : (
                    <Typography color="text.secondary">Indisponível no momento.</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <WhatsAppIcon sx={{ color: '#25D366', fontSize: 32 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      WhatsApp
                    </Typography>
                  </Box>
                  {loading ? (
                    <Skeleton variant="text" height={28} />
                  ) : contact?.whatsapp ? (
                    waHref ? (
                      <MuiLink href={waHref} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: '1.05rem' }}>
                        {contact.whatsapp}
                      </MuiLink>
                    ) : (
                      <Typography sx={{ fontSize: '1.05rem' }}>{contact.whatsapp}</Typography>
                    )
                  ) : (
                    <Typography color="text.secondary">Indisponível no momento.</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {!loading && !hasAnyContact && !error && (
            <Typography align="center" color="text.secondary" sx={{ mt: 3 }}>
              Nenhum canal cadastrado no momento. Utilize outros meios indicados no seu contrato ou na área logada, se aplicável.
            </Typography>
          )}
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button variant="text" onClick={() => navigate(ROUTES.HOME)} sx={{ mb: 2 }}>
          Voltar para a página inicial
        </Button>
        <Divider sx={{ mb: 3 }} />
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mb: 2 }}>
          Para solicitações relacionadas à LGPD ou dúvidas sobre privacidade, consulte também nossa{' '}
          <MuiLink component={RouterLink} to={ROUTES.PRIVACY_POLICY} underline="hover">
            Política de Privacidade
          </MuiLink>
          .
        </Typography>
      </Container>

      <Box sx={{ py: 3, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            <MuiLink component={RouterLink} to={ROUTES.PRIVACY_POLICY} underline="hover" color="text.secondary" sx={{ fontWeight: 600 }}>
              Política de Privacidade
            </MuiLink>
            <MuiLink component={RouterLink} to={ROUTES.ACCOUNT_DELETION_INFO} underline="hover" color="text.secondary" sx={{ fontWeight: 600 }}>
              Exclusão de conta
            </MuiLink>
          </Box>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 2 }}>
            © {new Date().getFullYear()} V-Condo. Todos os direitos reservados. Um produto VOVER Tecnologia e Serviços LTDA.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default ContactPage;
